import {
  Button,
  ColumnTable,
  DisplayInfo,
  Icon,
  Modal,
  Table,
  addToast,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Col, Container, Row } from 'reactstrap';

import Tooltip from '../../../components/tooltip/Tooltip';
import { TranslationsKeys } from '../../../locales/translations';
import { getBenefitAllocationHistory, getHistoryFileUrl } from '../api';
import { BenefitAllocationHistoryItem, BenefitKind } from '../types';

interface Props {
  isOpen: boolean;
  benefitKind: BenefitKind | null;
  onClose: () => void;
}

/**
 * Formato del XD: fecha y hora separadas por guion. El año va con 4 dígitos y
 * no con los 2 del mock, para no romper la convención del resto del producto.
 *
 * `uploadedAt` es un timestamp, no una fecha calendario: se formatea en hora
 * LOCAL y no con `utils/dates.formatDate`, que fuerza UTC. Con UTC una carga
 * hecha después de las 20:00 en Chile aparecería con la fecha del día
 * siguiente.
 */
function formatUploadedAt(value: string): string {
  const date = dayjs(value);

  return date.isValid() ? date.format('DD/MM/YYYY - HH:mm') : '-';
}

export default function AllocationHistoryModal({
  isOpen,
  benefitKind,
  onClose,
}: Props) {
  const { t } = useTranslation(
    TranslationsKeys.ADMISSION_AND_ENROLLMENTS_BENEFIT_ALLOCATION,
  );

  const [items, setItems] = useState<BenefitAllocationHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  /**
   * Beneficio que se está mostrando. Se conserva mientras el modal se cierra:
   * el padre pone `benefitKind` en null al cerrar y, como el `Modal` hace
   * fade-out, leerlo directo dejaba el subtítulo en blanco durante la
   * animación de salida.
   */
  const [displayedKind, setDisplayedKind] = useState<BenefitKind | null>(null);
  /**
   * Contador de la petición vigente. Cada carga lo incrementa, así una
   * respuesta lenta de la carga anterior se descarta en vez de pintar, por
   * ejemplo, el historial de CAE bajo el título de Gratuidad.
   */
  const requestIdRef = useRef<number>(0);

  const code = benefitKind?.code;

  const loadHistory = useCallback(async () => {
    if (!code) {
      return;
    }
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);
    setItems([]);
    setDownloadingId(null);
    const { data, error } = await getBenefitAllocationHistory(code);
    if (requestId !== requestIdRef.current) {
      return;
    }
    if (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('history.errorOnLoad'),
      });
      setItems([]);
    } else {
      setItems(data ?? []);
    }
    setLoading(false);
  }, [code, t]);

  useEffect(() => {
    // Al CERRAR no se toca el estado a propósito: el `Modal` hace fade-out y
    // cualquier cambio se ve durante la animación (limpiar `items` acá hacía
    // aparecer el estado vacío por un instante). La limpieza va al abrir,
    // dentro de `loadHistory`, que además invalida lo que quedó en vuelo.
    if (!isOpen || !benefitKind) {
      return;
    }
    setDisplayedKind(benefitKind);
    loadHistory();
  }, [isOpen, benefitKind, loadHistory]);

  const handleDownload = useCallback(
    async (batchId: number) => {
      setDownloadingId(batchId);
      const { data, error } = await getHistoryFileUrl(batchId);
      setDownloadingId(null);
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('history.errorOnDownload'),
        });

        return;
      }
      window.open(data.url, '_blank');
    },
    [t],
  );

  const columns = useMemo<ColumnTable<BenefitAllocationHistoryItem>[]>(
    () => [
      {
        columnName: 'uploadedAt',
        headerText: t('history.date'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => formatUploadedAt(row.uploadedAt),
      },
      {
        columnName: 'uploadedBy',
        headerText: t('history.uploadedBy'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => row.uploadedBy ?? '-',
      },
      {
        columnName: 'academicYear',
        headerText: t('history.academicYear'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        // Sin esta columna no hay forma de saber qué carga rige: la fecha no
        // alcanza, porque re-cargar el mismo año reemplaza el lote y dos
        // cargas del mismo minuto se ven idénticas.
        columnName: 'isActive',
        headerText: t('history.status'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <Badge
            className="status-badge fs-14 fw-400 py-2 px-3 text-dark"
            style={{ backgroundColor: row.isActive ? '#CEF0DA' : '#DDEFFF' }}
            pill
          >
            {row.isActive ? t('history.active') : t('history.replaced')}
          </Badge>
        ),
      },
      {
        columnName: 'download',
        headerText: t('history.download'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          // Sin archivo el botón va deshabilitado y con tooltip explicativo;
          // el tooltip cuelga del wrapper y no del botón porque un control
          // deshabilitado no despacha eventos de mouse.
          if (!row.hasFile) {
            return (
              <Tooltip
                id={`benefit-allocation-history-download-${row.id}`}
                text={t('history.withoutFile')}
                placement="top"
              >
                <Button
                  size="sm"
                  icon="download"
                  text={t('history.download')}
                  disabled
                />
              </Tooltip>
            );
          }

          return (
            <Button
              size="sm"
              icon="download"
              text={t('history.download')}
              loading={downloadingId === row.id}
              onClick={() => handleDownload(row.id)}
            />
          );
        },
      },
    ],
    [t, downloadingId, handleDownload],
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h1 className="text-dark fs-22 text-center mb-2">
              {t('history.title')}
            </h1>
          </Col>
        </Row>
        {displayedKind && (
          <Row>
            <Col
              xs={12}
              className="d-flex justify-content-center align-items-center mb-4"
            >
              <Icon name="information" color="primary" size="16px" />
              <span className="fs-16 ml-2">
                {t(`kinds.${displayedKind.code}.shortName`)}
              </span>
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={12}>
            <Table
              columns={columns}
              data={items}
              isLoadingResults={loading}
              noResultsText={
                <div className="d-flex justify-content-center align-items-center py-4">
                  <DisplayInfo
                    iconName="information"
                    textBody={t('history.noResults')}
                    maxWidth="500px"
                  />
                </div>
              }
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={12}>
            <Button
              type="button"
              size="md"
              fullwidth
              text={t('history.close')}
              onClick={onClose}
            />
          </Col>
        </Row>
      </Container>
    </Modal>
  );
}
